import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Users, UserCircle2, UserPlus, Trash2 } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import { useAuth } from '@/OS/hooks/useAuth';
import { Database } from '@/OS/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';

type LegacyProfileRow = Database['public']['Tables']['profiles']['Row'];
type ProfileRow = LegacyProfileRow & {
  full_name?: string | null;
  email?: string | null;
  role?: string | null;
};

interface UserSettingsProps {
  onOpenWindow?: (appId: string, title: string) => void;
}

export const UserSettings: React.FC<UserSettingsProps> = ({ onOpenWindow }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [currentProfile, setCurrentProfile] = useState<ProfileRow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Local UI state (design unchanged)
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [fastUserSwitching, setFastUserSwitching] = useState(true);
  const [guestAccess, setGuestAccess] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);

  // Load the signed-in user's profile
  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      if (!user?.id) {
        setCurrentProfile(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!isMounted) return;

      if (error) {
        console.warn('Failed to load profile:', error);
        setCurrentProfile(null);
      } else {
        setCurrentProfile(data);
      }
      setLoading(false);
    }

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // Map current profile into the existing card shape (design preserved)
  const users = useMemo(() => {
    if (!currentProfile) return [];

    const fullName =
      currentProfile.full_name ||
      currentProfile.username ||
      currentProfile.email ||
      'User';

    const role = (currentProfile.role || 'user').toLowerCase();
    const status = 'active' as const;

    return [
      {
        id: currentProfile.id,
        name: fullName,
        email: currentProfile.email || '',
        role: role as 'admin' | 'user' | 'guest',
        avatar: currentProfile.avatar_url || '',
        status,
        lastLogin: '—',
      },
    ];
  }, [currentProfile]);

  // Actions (coming-soon behavior)
  const addUser = async () => {
    if (!newUserName || !newUserEmail) return;

    // Validate against main site: profile must exist by email
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, avatar_url, role')
      .ilike('email', newUserEmail.trim());

    if (error) {
      toast({
        title: 'Unable to verify user',
        description: 'Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    if (!data || data.length === 0) {
      toast({
        title: 'User not found on main site',
        description: 'Ask the user to create an account first.',
        variant: 'destructive',
      });
      return;
    }

    // User exists — show "coming soon" since multi-user is not active yet
    toast({
      title: 'User verified',
      description:
        'Multi-user management is coming soon. We verified this user exists and will enable linking in a future update.',
    });

    // Reset inputs
    setNewUserName('');
    setNewUserEmail('');
    setNewUserRole('user');
  };

  const removeUser = (_userId: string) => {
    toast({
      title: 'Coming soon',
      description:
        'Removing additional users from this device will be available in a future update.',
    });
  };

  const toggleUserStatus = (_userId: string) => {
    toast({
      title: 'Coming soon',
      description:
        'Temporarily disabling a user will be available in a future update.',
    });
  };

  const changeUserRole = (_userId: string, _newRole: string) => {
    toast({
      title: 'Coming soon',
      description: 'Role changes will be available in a future update.',
    });
  };

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">User Settings</h1>
            <p className="text-muted-foreground">
              Manage your account and see verified users from our main site
            </p>
          </div>
          <Button
            onClick={() => onOpenWindow?.('profile', 'Profile')}
            className="gap-2"
          >
            <UserCircle2 size={18} />
            Open Profile
          </Button>
        </div>

        {/* Current Users (now uses real data for the signed-in user) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              User Accounts
            </CardTitle>
            <CardDescription>
              Your MapleOS account details (additional users coming soon)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && (
              <div className="text-sm text-muted-foreground">Loading your account…</div>
            )}
            {!loading && users.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No user is currently signed in.
              </div>
            )}
            {!loading &&
              users.map((userItem) => (
                <div key={userItem.id} className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <Avatar>
                      <AvatarImage src={userItem.avatar || undefined} />
                      <AvatarFallback>
                        {userItem.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{userItem.name}</span>
                        {userItem.role === 'admin' && (
                          <Badge variant="secondary" className="text-xs">
                            Admin
                          </Badge>
                        )}
                        <Badge
                          variant={userItem.status === 'active' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {userItem.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">{userItem.email}</div>
                      <div className="text-xs text-muted-foreground">
                        Last login: {userItem.lastLogin}
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => onOpenWindow?.('profile', 'Profile')}>
                    Open Profile
                  </Button>
                </div>
              ))}
          </CardContent>
        </Card>

        {/* Add New User (validate against main site; coming soon after verification) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Add New User
            </CardTitle>
            <CardDescription>
              Add a user that already exists on our main site (verification only for now)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-user-name">Full Name</Label>
                <Input
                  id="new-user-name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Enter user's full name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-user-email">Email Address</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-user-role">User Role</Label>
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="user">Standard User</SelectItem>
                  <SelectItem value="guest">Guest User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={addUser}
              disabled={!newUserName || !newUserEmail}
              className="w-full"
            >
              <UserPlus size={16} className="mr-2" />
              Add User
            </Button>
          </CardContent>
        </Card>

        {/* Account Options (unchanged visual, local toggles for now) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Account Options
            </CardTitle>
            <CardDescription>
              Configure user account behavior and permissions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="fast-user-switching">Fast User Switching</Label>
                <p className="text-sm text-muted-foreground">
                  Allow switching between users without logging out
                </p>
              </div>
              <Switch
                id="fast-user-switching"
                checked={fastUserSwitching}
                onCheckedChange={(v) => {
                  setFastUserSwitching(v);
                  toast({ title: 'Saved', description: 'Fast User Switching updated.' });
                }}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="guest-access">Allow Guest Access</Label>
                <p className="text-sm text-muted-foreground">
                  Enable temporary guest accounts for visitors
                </p>
              </div>
              <Switch
                id="guest-access"
                checked={guestAccess}
                onCheckedChange={(v) => {
                  setGuestAccess(v);
                  toast({ title: 'Saved', description: 'Guest Access updated.' });
                }}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="auto-login">Automatic Login</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically log in the last user at startup
                </p>
              </div>
              <Switch
                id="auto-login"
                checked={autoLogin}
                onCheckedChange={(v) => {
                  setAutoLogin(v);
                  toast({ title: 'Saved', description: 'Automatic Login updated.' });
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Default User Permissions, Recent Activity, and footer actions — unchanged visuals */}
        <Card>
          <CardHeader>
            <CardTitle>Default User Permissions</CardTitle>
            <CardDescription>
              Set default permissions for new user accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <h4 className="font-medium">System Access</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Install software</Label>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Change system settings</Label>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Access Control Panel</Label>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Modify user accounts</Label>
                    <Switch />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">File System</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Access all drives</Label>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Modify system files</Label>
                    <Switch />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Create/delete files</Label>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Change file permissions</Label>
                    <Switch />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Monitor user account activity and login history
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <div className="font-medium text-sm">Signed in</div>
                    <div className="text-xs text-muted-foreground">—</div>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs">Login</Badge>
              </div>
            </div>

            <div className="pt-4">
              <Button variant="outline" size="sm" onClick={() => toast({ title: 'Coming soon', description: 'Full activity log will arrive in a future update.' })}>
                View Full Activity Log
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => toast({ title: 'Export ready', description: 'Your account export is being prepared (coming soon).' })}>
            Export User Data
          </Button>
          <Button onClick={() => toast({ title: 'Saved', description: 'Your preferences have been saved.' })}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};